import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, UserPlus, Sparkles, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import ConsentModal from '@/components/ConsentModal'
import api from '@/api/client'
import { toast } from '@/components/ui/toaster'
import HCaptchaField, { hCaptchaSiteKey } from '@/components/HCaptchaField'

export default function Register() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showConsent, setShowConsent] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')

  const passwordChecks = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!Object.values(passwordChecks).every(Boolean)) {
      toast({ title: t('auth.passwordRequirementsError'), variant: 'destructive' })
      return
    }
    if (hCaptchaSiteKey && !captchaToken) {
      toast({ title: 'Lütfen güvenlik doğrulamasını tamamlayın.', variant: 'destructive' })
      return
    }
    // Show consent modal before submitting
    setShowConsent(true)
  }

  const handleConsentAccept = async (consents: { termsAccepted: boolean; privacyAccepted: boolean; cookieAccepted: boolean; marketingEmails: boolean }) => {
    setShowConsent(false)
    setLoading(true)
    try {
      await api.post('/auth/register', {
        username,
        email,
        password,
        ...consents,
        hcaptchaResponse: captchaToken,
        termsVersion: '1.0',
        privacyVersion: '1.0',
        cookieVersion: '1.0',
      })
      toast({ title: t('auth.registerSuccess') })
      navigate('/login')
    } catch (err: any) {
      toast({
        title: t('auth.registerFailed'),
        description: err.response?.data?.error || t('common.somethingWentWrong'),
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <h1 className="font-display text-2xl font-bold">{t('auth.joinCommunity')}</h1>
          <p className="text-muted-foreground mt-2">{t('auth.registerSubtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <HCaptchaField onToken={setCaptchaToken} />
          <div>
            <label className="block text-sm font-medium mb-2">{t('auth.username')}</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              maxLength={30}
              className="w-full px-4 py-3 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder={t('auth.usernamePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('auth.email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
              placeholder={t('auth.emailPlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">{t('auth.password')}</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 rounded-lg bg-secondary border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 pr-12"
                placeholder={t('auth.passwordPlaceholder')}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <div className="mt-2 space-y-1">
              {Object.entries(passwordChecks).map(([key, valid]) => (
                <div key={key} className={`flex items-center gap-2 text-xs ${valid ? 'text-green-500' : 'text-muted-foreground'}`}>
                  {valid ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                  {key === 'length' && t('validation.minLength')}
                  {key === 'upper' && t('validation.uppercase')}
                  {key === 'lower' && t('validation.lowercase')}
                  {key === 'number' && t('validation.number')}
                  {key === 'special' && t('validation.special')}
                </div>
              ))}
            </div>
          </div>

          <Button type="submit" className="w-full rounded-lg" disabled={loading}>
            <UserPlus className="w-4 h-4 mr-2" />
            {loading ? t('common.loading') : t('nav.register')}
          </Button>
        </form>

        <p className="text-center mt-6 text-sm text-muted-foreground">
          {t('auth.hasAccount')}{' '}
          <Link to="/login" className="text-primary hover:underline">
            {t('auth.loginNow')}
          </Link>
        </p>
      </motion.div>

      <ConsentModal
        open={showConsent}
        onAccept={handleConsentAccept}
        required
      />
    </div>
  )
}
